import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useMatches, useNavigationType } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useBreadcrumbs } from 'contexts/BreadcrumbsProvider';
import type { CrumbItem } from 'contexts/BreadcrumbsProvider';

const extraAgendaChild = (
  currentPath: string,
  items: CrumbItem[],
  baseLength: number,
  errorMessage: string
): { id: string | null; extrasMap: Record<string, CrumbItem> } => {
  const extras: Record<string, CrumbItem> = {};
  if (baseLength < 1 || baseLength === items.length || items.length < 2) {
    toast.error(errorMessage);
    return { id: null, extrasMap: extras };
  }
  // Extras start before the last segment of breadcrumbs, thus the -1
  for (let i = baseLength - 1; i < items.length - 1; i++) {
    const item = items[i];
    extras[item.id] = {
      id: item.id,
      label: item.label,
      path: item.path,
    };
  }

  const extrasArray = Object.values(extras);

  for (const extra of extrasArray) {
    if (currentPath.startsWith(extra.path)) {
      return { id: extra.id, extrasMap: extras };
    }
  }

  return { id: null, extrasMap: extras };
};

export function useBreadcrumbsBrowserNavigation() {
  const {
    items,
    baseLength,
    remove,
    pushRemoved,
    removedExtras,
    restoreLatestExtra,
    clickedBreadcrumb,
    setClickedBreadcrumb,
    restoreAllRemovedExtras,
    extras,
    pushRemovedByIds,
    clickedBreadcrumbsAndThenBack,
    setClickedBreadcrumbsAndThenBack,
  } = useBreadcrumbs();
  const location = useLocation();
  const matches = useMatches();
  const navigationType = useNavigationType();
  const { t } = useTranslation();
  const previousPathname = useRef<string | null>(null);

  useEffect(() => {
    const pathnameChanged = previousPathname.current !== location.pathname;
    previousPathname.current = location.pathname;
    if (!pathnameChanged) return;
    if (navigationType !== 'POP') {
      return;
    }

    const extrasLength = items.length - baseLength;

    // If we previously clicked a breadcrumb, the browser navigation direction must be back
    if (clickedBreadcrumb) {
      restoreAllRemovedExtras();
      setClickedBreadcrumb(false);
      setClickedBreadcrumbsAndThenBack(true);
      return;
    }

    // Back navigation
    if (extrasLength > 0 && items.length >= 2 && !clickedBreadcrumbsAndThenBack) {
      const { id: parentExtraId, extrasMap } = extraAgendaChild(location.pathname, items, baseLength, t('toast.error.invalidBreadcrumbStructure'));

      if (parentExtraId) {
        pushRemoved(extrasMap[parentExtraId]);
        remove([parentExtraId]);
        return;
      }
    }

    // Forward navigation
    if (removedExtras.length > 0) {
      const lastRemovedExtra = removedExtras[removedExtras.length - 1];

      const agendaIdMatch = lastRemovedExtra.id.match(/^parent-agenda-(.+)$/);

      if (agendaIdMatch) {
        const removedAgendaId = agendaIdMatch[1];
        const flowMatch = location.pathname.includes(`/Flow/${removedAgendaId}/`);
        const heliosMatch = location.pathname.includes(`/Helios/${removedAgendaId}/`);

        if (flowMatch || heliosMatch) {
          restoreLatestExtra();
        }
      }
    }

    // Edge case: if we navigated back to a non-extra breadcrumb after having clicked a breadcrumb and then used browser back navigation
    if (clickedBreadcrumbsAndThenBack) {
      setClickedBreadcrumbsAndThenBack(false);

      for (const m of matches) {
        const maybeExtraPath = /(?:^|\/)(Flow|Helios)(?:\/)/.test(m.pathname);

        if (!maybeExtraPath) {
          continue;
        }
        for (const e of Object.values(extras)) {
          if (e.path.startsWith(m.pathname)) {
            const orderedIds = Object.keys(extras);
            const startIndex = orderedIds.indexOf(e.id);

            if (startIndex !== -1) {
              const idsToRemove = orderedIds.slice(startIndex);
              pushRemovedByIds(idsToRemove);
              remove(idsToRemove);
            }
            break;
          }
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, navigationType, remove, restoreLatestExtra]);
}
